import React, { useState, useEffect } from 'react';
import { Language } from '../types';

interface GenerationProgressProps {
  lang: Language;
  statusMessages: { zh: string[]; en: string[] };
  onComplete?: () => void;
}

const GenerationProgress: React.FC<GenerationProgressProps> = ({ 
  lang, 
  statusMessages,
  onComplete 
}) => {
  const [renderProgress, setRenderProgress] = useState(0);
  const [renderStatusIndex, setRenderStatusIndex] = useState(0);

  useEffect(() => {
    let progressInterval: NodeJS.Timeout;
    let statusInterval: NodeJS.Timeout;

    setRenderProgress(0);
    setRenderStatusIndex(0);

    // Simulated progress: fast at first, then slow
    progressInterval = setInterval(() => {
      setRenderProgress(prev => {
        if (prev < 40) return prev + Math.random() * 5;
        if (prev < 85) return prev + Math.random() * 2;
        if (prev < 98) return prev + Math.random() * 0.5;
        return prev;
      });
    }, 500);

    // Rotate status messages
    statusInterval = setInterval(() => {
      setRenderStatusIndex(prev => (prev + 1) % statusMessages[lang].length);
    }, 3000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(statusInterval);
    };
  }, [lang, statusMessages]);

  return (
    <div className="absolute inset-0 z-20 bg-slate-950/90 flex flex-col items-center justify-center p-8">
      <div className="w-full max-w-[240px] flex flex-col items-center gap-4">
        <div className="flex justify-between w-full mb-1">
          <span className="text-[10px] font-black text-white/40 tracking-[0.1em]">
            {lang === 'zh' ? '正在生成视觉方案' : 'Generating Visuals'}
          </span>
          <span className="text-[10px] font-mono text-red-500 font-bold">{Math.floor(renderProgress)}%</span>
        </div>
        <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-500 ease-out relative"
            style={{ width: `${renderProgress}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
          </div>
        </div>
        <p className="text-[10px] text-white/60 font-medium text-center animate-pulse min-h-[1.5em]">
          {statusMessages[lang][renderStatusIndex]}
        </p>
      </div>
    </div>
  );
};

export default React.memo(GenerationProgress);
